import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import {  generateVoucher, getVoucher } from '../../components/actions/voucher';
import Loader from '../../components/utils/Loader';

export default function VouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');


  useEffect(() => {
    setLoading(true);
    const merchantCode = sessionStorage.getItem('code');
    getVoucher(merchantCode)
      .then((res) => {
        const tempData = res?.data?.map((val) => ({
          id: val.code || ``,
          name: val.name || '-',
          templateCode: val.code || '-',
          startDate: val.startDate || new Date().toISOString().split('T')[0],
          endDate: val.endDate || new Date().toISOString().split('T')[0],
          generatedType: val.voucherMode === 'G' ? 'Generated' : 'Imported',
          voucherMode: val.voucherMode, // Added to store raw voucherMode
          status: val.status,
          type: val.type || '',
          discount: val.discount || '0',
          totalCount: val.totalCount || 0,
          usedCount: val.usedCount || 0,
          totalVoucher:val?.totalVoucher || 0,
          assignVoucherCount:val?.assignVoucherCount || 0,
          description: val.description || 'No description available',
        }));
        setVouchers(tempData || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching vouchers:', err);
        setLoading(false);
      });
  }, []);

  // Filter vouchers based on search, status, and type
  const filteredVouchers = useMemo(() => {
    return vouchers.filter((voucher) => {
      const matchesSearch =
        voucher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        voucher.templateCode.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === 'All Status' ||
        (statusFilter === 'Active' && voucher.status) ||
        (statusFilter === 'Inactive' && !voucher.status);
      const matchesType = typeFilter === 'All Types' || voucher.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [vouchers, searchTerm, statusFilter, typeFilter]);

  const totalPages = Math.ceil(filteredVouchers.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedVouchers = filteredVouchers.slice(startIndex, startIndex + entriesPerPage);

  // Calculate statistics
  const totalVouchers = vouchers.length;
  const activeVouchers = vouchers.filter((v) => v.status).length;
  const totalUsage = vouchers.reduce((sum, v) => sum + v.usedCount, 0);
  const totalAvailable = vouchers.reduce((sum, v) => sum + v.totalCount, 0);
  const avgUsageRate = totalAvailable ? Math.round((totalUsage / totalAvailable) * 100) : 0;

  const viewVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setShowViewModal(true);
  };

  const handleFileUpload = (voucher) => {
    setSelectedVoucher(voucher);
    setShowUploadModal(true);
  };

  const handleGenerateVoucher = async (voucher) => {
    setLoading(true);
    const merchantCode = sessionStorage.getItem('code');
    try {
      const response = await generateVoucher(voucher.voucherMode, merchantCode, voucher.templateCode, null);
      if (response.message === 'success') {
        setUploadStatus('success');
        setStatusMessage('Voucher generated successfully!');
      } else {
        setUploadStatus('error');
        setStatusMessage('Failed to generate voucher. Please try again.');
      }
    } catch (error) {
      setUploadStatus('error');
      setStatusMessage(`Error generating voucher: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSubmit = async () => {
    if (!selectedFile || !selectedVoucher) return;
    setLoading(true);
    const merchantCode = sessionStorage.getItem('code');
    try {
      const response = await generateVoucher(selectedVoucher.voucherMode, merchantCode, selectedVoucher.templateCode, selectedFile);
      if (response.message === 'success') {
        setUploadStatus('success');
        setStatusMessage('File uploaded and voucher generated successfully!');
      } else {
        setUploadStatus('error');
        setStatusMessage('Failed to upload file or generate voucher. Please try again.');
      }
    } catch (error) {
      setUploadStatus('error');
      setStatusMessage(`Error uploading file: ${error.message}`);
    } finally {
      setLoading(false);
      setSelectedFile(null);
    }
  };

  const getStatusColor = (status) => {
    return status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Percentage':
        return 'bg-blue-100 text-blue-800';
      case 'Fixed Amount':
        return 'bg-purple-100 text-purple-800';
      case 'Free Shipping':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGeneratedTypeColor = (type) => {
    switch (type) {
      case 'G':
        return 'bg-green-100 text-green-800';
      case 'I':
        return 'bg-blue-100 text-blue-800';
      case 'Bulk':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <DashboardLayout>
      {loading && <Loader />}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Voucher Template Management</h1>
            <p className="text-gray-600 mt-1">Create and manage discount vouchers for your customers</p>
          </div>
          <Link
            to="/create-voucher"
            className="bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center gap-2 whitespace-nowrap"
            aria-label="Add new voucher"
          >
            <i className="ri-add-line text-lg"></i>
            Add New Voucher Template
          </Link>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-coupon-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Vouchers</p>
                <p className="text-2xl font-bold text-gray-900">{totalVouchers}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +12% this month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-check-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Vouchers</p>
                <p className="text-2xl font-bold text-gray-900">{activeVouchers}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +8% growth</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-gift-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Usage</p>
                <p className="text-2xl font-bold text-gray-900">{totalUsage.toLocaleString('en-IN')}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +15% usage</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-percent-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Usage Rate</p>
                <p className="text-2xl font-bold text-gray-900">{avgUsageRate}%</p>
                <p className="text-green-600 text-xs font-semibold">↗ +5% effective</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative w-full lg:w-80">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search vouchers by name or template code..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200 text-sm bg-gray-50"
                aria-label="Search vouchers"
              />
            </div>

            <div className="flex gap-3 items-center w-full lg:w-auto">
              <div className="relative w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer w-full sm:w-auto"
                  aria-label="Filter by status"
                >
                  <option value="All Status">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>

              <div className="relative w-full sm:w-auto">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer w-full sm:w-auto"
                  aria-label="Filter by type"
                >
                  <option value="All Types">All Types</option>
                  <option value="Percentage">Percentage</option>
                  <option value="Fixed Amount">Fixed Amount</option>
                  <option value="Free Shipping">Free Shipping</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer pr-8"
                aria-label="Select entries per page"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">entries</span>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredVouchers.length} results
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template Code
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Generated Vouchers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Generated Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedVouchers.map((voucher) => (
                  <tr key={voucher.id} className="hover:bg-gray-50">
                    {console.log(voucher)}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                       
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{voucher.name}</p>
                          {/* <p className="text-xs text-gray-500">{voucher.discount} • {voucher.type}</p> */}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {voucher.templateCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
  <div className="space-y-1">
    <div>
      <span className="font-semibold">No. of Generated Vouchers:</span> {voucher?.totalVoucher}
    </div>
    <div>
      <span className="font-semibold text-green-600">No. of Used Vouchers:</span> {voucher?.assignVoucherCount}
    </div>
    <div>
      <span className="font-semibold text-red-600">No. of Un-used Vouchers:</span> {(voucher?.totalVoucher) - (voucher?.assignVoucherCount)}
    </div>
  </div>
</td>

                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(voucher.startDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </td> */}
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(voucher.endDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getGeneratedTypeColor(
                          voucher.generatedType
                        )}`}
                      >
                        {voucher.generatedType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(voucher.status)}`}
                      >
                        {voucher.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewVoucher(voucher)}
                          className="text-blue-600 hover:text-blue-800"
                          aria-label={`View voucher ${voucher.id}`}
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        <Link
                          to={`/edit-voucher/${voucher.templateCode}`}
                          className="text-green-600 hover:text-green-800"
                          aria-label={`Edit voucher ${voucher.id}`}
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </Link>
                        {voucher.voucherMode === 'G' ? (
                          <button
                            onClick={() => handleGenerateVoucher(voucher)}
                            className="text-purple-600 hover:text-purple-800"
                            aria-label={`Generate voucher ${voucher.id}`}
                          >
                            <i className="ri-magic-line text-lg"></i>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleFileUpload(voucher)}
                            className="text-blue-600 hover:text-blue-800"
                            aria-label={`Upload voucher ${voucher.id}`}
                          >
                            <i className="ri-upload-2-line text-lg"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredVouchers.length)} of{' '}
              {filteredVouchers.length} entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const pageNum = index + 1 + Math.max(0, currentPage - 3);
                if (pageNum > totalPages) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    aria-label={`Go to page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* View Voucher Modal */}
        {showViewModal && selectedVoucher && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Voucher Template Details</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close modal"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <i className="ri-coupon-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{selectedVoucher.name}</h3>
                      <p className="text-gray-600">{selectedVoucher.description}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Template Code</span>
                        <span className="font-medium font-mono">{selectedVoucher.templateCode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeColor(
                            selectedVoucher.type
                          )}`}
                        >
                          {selectedVoucher.type}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Discount</span>
                        <span className="font-semibold text-blue-600">{selectedVoucher.discount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            selectedVoucher.status
                          )}`}
                        >
                          {selectedVoucher.status ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Usage Statistics</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Count</span>
                        <span className="font-medium">{selectedVoucher.totalCount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Used Count</span>
                        <span className="font-medium">{selectedVoucher.usedCount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Usage Rate</span>
                        <span className="font-semibold text-green-600">
                          {selectedVoucher.totalCount
                            ? Math.round((selectedVoucher.usedCount / selectedVoucher.totalCount) * 100)
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Generated Type</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getGeneratedTypeColor(
                            selectedVoucher.generatedType
                          )}`}
                        >
                          {selectedVoucher.generatedType}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Validity Period</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-600 text-sm">Start Date</span>
                      <p className="font-medium">
                        {new Date(selectedVoucher.startDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-sm">End Date</span>
                      <p className="font-medium">
                        {new Date(selectedVoucher.endDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors"
                    aria-label="Close voucher details"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Voucher Modal */}
        {showUploadModal && selectedVoucher && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Upload Voucher File</h2>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close upload modal"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File to Upload
                </label>
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  aria-label="Upload voucher file"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                  aria-label="Cancel upload"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFileSubmit}
                  disabled={!selectedFile}
                  className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                    selectedFile
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  aria-label="Submit file"
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Popup */}
        {uploadStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {uploadStatus === 'success' ? 'Success' : 'Error'}
                </h2>
                <button
                  onClick={() => setUploadStatus(null)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close status popup"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <p className={`text-sm ${uploadStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {statusMessage}
              </p>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setUploadStatus(null)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-blue-600 transition-colors"
                  aria-label="Close status message"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}